<script setup lang="ts">
// NPM
import { computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { readSupportReference } from '@/services/access-unavailable';

// Components
import { BaseButton } from '@magma-app/magma-lapilli';

const props = defineProps<{ variant?: 'invitation'; retrying?: boolean }>();
const emit = defineEmits<{ (event: 'retry'): void }>();

// Stores
import { useAuthStore } from '@/stores/auth';
const authStore = useAuthStore();

// Plugins
const { locale } = useI18n();
const route = useRoute();
const reason = computed(() => ['account', 'relation', 'program'].includes(String(route.query.reason))
  ? String(route.query.reason) : 'account');
const supportReference = computed(() => readSupportReference(route.query.ref));

// Lifecycle hooks
onBeforeMount(async () => {
  if (props.variant === 'invitation') return;
  // Show the organization's logo and language when reached from its subdomain
  const subdomain = window.location.host.split('.')[0];

  if (subdomain && subdomain != 'v3') {
    try {
      await authStore.getSignInData(subdomain, '');
    } catch {
      // Branding is optional here: the message must show even if this fails
    }

    if (authStore.signInData) {
      locale.value = authStore.signInData.language;
    }
  }
});
</script>

<template>
  <div class="gradient__grey__07 flex min-h-dvh flex-col items-center justify-center p-8">
    <div class="sm:w-[360px]">
      <img
        class="mx-auto h-20 w-20 rounded-full object-cover ring-[5px] ring-border-on-emphasis"
        :alt="variant === 'invitation' ? 'Magma' : authStore.signInData?.name || 'Magma'"
        :src="
          variant === 'invitation'
            ? '/img/logo_magma.png'
            : authStore.signInData?.logoUrl || '/img/logo_magma.png'
        "
      />

      <h1
        class="mx-auto mt-8 text-center text-mgm-txt-md font-medium text-foreground-emphasis"
      >
        {{ variant === 'invitation' ? $t('referral.inviteUnavailableTitle') : $t(`unavailableAccess.${reason}.title`) }}
      </h1>

      <p class="mx-auto mt-3 text-center text-mgm-txt-sm text-foreground-default">
        {{ variant === 'invitation' ? $t('referral.inviteUnavailable') : $t(`unavailableAccess.${reason}.description`) }}
      </p>

      <p v-if="supportReference && !variant" class="mt-4 select-all break-words text-center text-mgm-txt-xs text-foreground-subtle">
        <span class="block">{{ $t('unavailableAccess.referenceLabel') }}</span>
        <span class="mt-1 block font-mono">{{ supportReference }}</span>
      </p>

      <BaseButton
        class="mt-8 w-full"
        :text="variant === 'invitation' ? $t('sessionRecovery.retry') : $t('generic.backToLogin')"
        :disabled="retrying"
        :loading="retrying"
        size="md"
        type="button"
        @click="variant === 'invitation' ? emit('retry') : $router.push({ name: 'login' })"
      />
    </div>
  </div>
</template>
